/**
 * Bandeau d'état de la connexion.
 *
 * Il expose délibérément ce que les services équivalents taisent : le type de chemin
 * réseau réellement utilisé et la latence au serveur de signaling. Un utilisateur dont
 * le transfert est lent doit pouvoir distinguer « ma connexion est relayée » de
 * « mon correspondant a une mauvaise liaison montante ».
 */

import { useT, type MessageKey } from '../i18n/index.js';
import { describeConnectionKey, useAppStore } from '../store.js';
import type { SignalingStatus } from '../core/signaling.js';

function signalingLabelKey(status: SignalingStatus): {
  key: MessageKey;
  tone: string;
  pulse: boolean;
} {
  switch (status) {
    case 'open':
      return { key: 'status.connected', tone: 'success', pulse: false };
    case 'connecting':
      return { key: 'status.connecting', tone: 'warning', pulse: true };
    case 'reconnecting':
      return { key: 'status.reconnecting', tone: 'warning', pulse: true };
    case 'failed':
      return { key: 'status.refused', tone: 'danger', pulse: false };
    case 'closed':
      return { key: 'status.disconnected', tone: 'danger', pulse: false };
    default:
      return { key: 'status.waiting', tone: 'muted', pulse: false };
  }
}

export function StatusBar(): React.JSX.Element {
  const signalingStatus = useAppStore((state) => state.signalingStatus);
  const latencyMs = useAppStore((state) => state.latencyMs);
  const connection = useAppStore((state) => state.connection);
  const peer = useAppStore((state) => state.peer);
  const phase = useAppStore((state) => state.phase);
  const translator = useT();

  const signaling = signalingLabelKey(signalingStatus);
  const showConnection = phase === 'transferring' || phase === 'done' || connection !== null;

  return (
    <div className="row" style={{ gap: '0.5rem' }}>
      <span className="badge" data-tone={signaling.tone}>
        <span className="dot" data-pulse={signaling.pulse} aria-hidden="true" />
        {translator.t(signaling.key)}
      </span>

      {showConnection && (
        <span className="badge" data-tone={connection?.throughServer ? 'warning' : 'success'}>
          {translator.t(describeConnectionKey(connection))}
        </span>
      )}

      {peer && (
        <span className="badge">
          {peer.role === 'sender'
            ? translator.t('status.roleSender')
            : translator.t('status.roleReceiver')}
          {peer.name ? ` : ${peer.name}` : ''}
        </span>
      )}

      {latencyMs !== null && <span className="badge">{latencyMs} ms</span>}
    </div>
  );
}
